/**
 * ScopeSelector — hierarchical catalog tree (Phase 3 sidebar redesign).
 *
 * Driven by the backend `catalog` facet's hierarchical FacetBucket[]:
 *   - top-level rows: 'own' (My Catalog), each 'delegate:<o>:<c>' (delegated
 *     catalog by name), and a leaf 'public' row (system-wide public).
 *   - each non-leaf row expands into two children (Public / Private) carrying
 *     their own counts.
 *
 * Selection canonicalization lives in `canonicalizeScopes` so the URL is
 * deterministic regardless of click order:
 *   - parent checked → bare scope token
 *   - one child checked → narrowed scope token (parent renders indeterminate)
 *   - both children checked → collapses to bare parent
 *
 * Empty-selection contract: emitting [] tells SearchContext to OMIT the
 * `?scopes=` param entirely (backend rejects present-but-empty with 400).
 *
 * Graceful rollout: when `catalogBuckets` is undefined (e.g. backend hasn't
 * delivered the new agg yet, or no facets requested), the component falls
 * back to a flat 3-row layout (My Catalog / Public / Shared With Me) without
 * counts.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormGroup,
  IconButton,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import {
  canonicalizeScopes,
  type SearchScope,
  type Visibility,
} from '../../utils/searchScopes';
import type { FacetBucket } from '../../contexts/SearchContext';

// -- Types ------------------------------------------------------------------

interface ParentRow {
  /** Scope token for the bare parent. */
  parent: SearchScope;
  /** Stable testid/key segment (also the URL token for non-narrowed scopes). */
  key: string;
  /** Display label. */
  label: string;
  /** Bucket count for the parent row. */
  count?: number;
  /** Whether this row supports Public/Private children. */
  expandable: boolean;
  /** Counts keyed by visibility (only when `expandable`). */
  childCounts?: Partial<Record<Visibility, number>>;
}

export interface ScopeSelectorProps {
  value: SearchScope[];
  onChange: (next: SearchScope[]) => void;
  /** Backend's `catalog` facet payload. Omit while rolling out. */
  catalogBuckets?: FacetBucket[] | null;
}

// -- Bucket → row adapter ---------------------------------------------------

const FALLBACK_ROWS: ParentRow[] = [
  { parent: 'own', key: 'own', label: 'My Catalog', expandable: true },
  { parent: 'delegate', key: 'delegate', label: 'Shared With Me', expandable: false },
  { parent: 'public', key: 'public', label: 'Public', expandable: false },
];

function bucketsToRows(buckets: FacetBucket[]): ParentRow[] {
  const rows: ParentRow[] = [];
  for (const b of buckets) {
    const childCounts: Partial<Record<Visibility, number>> = {};
    for (const child of b.children ?? []) {
      // Child wire forms: `<parent>:public` / `<parent>:private`.
      const suffix = child.value.slice(b.value.length + 1);
      if (suffix === 'public' || suffix === 'private') {
        childCounts[suffix] = child.count;
      }
    }
    if (b.value === 'own') {
      rows.push({
        parent: 'own',
        key: 'own',
        label: 'My Catalog',
        count: b.count,
        expandable: !!b.children,
        childCounts,
      });
    } else if (b.value === 'public') {
      rows.push({
        parent: 'public',
        key: 'public',
        label: 'Public',
        count: b.count,
        expandable: false,
      });
    } else if (b.value.startsWith('delegate:')) {
      const parts = b.value.split(':');
      // delegate:<ownerId>:<collectionId>
      if (parts.length === 3) {
        const [, ownerId, collectionId] = parts;
        rows.push({
          parent: { kind: 'delegate', ownerId, collectionId },
          key: b.value,
          label: b.label || 'Shared catalog',
          count: b.count,
          expandable: !!b.children,
          childCounts,
        });
      }
    }
  }
  return rows;
}

// -- Selection state helpers -------------------------------------------------

function scopeMatchesParent(scope: SearchScope, row: ParentRow): boolean {
  if (typeof scope === 'string') {
    if (scope === 'own' && row.key === 'own') return true;
    if (scope === 'public' && row.key === 'public') return true;
    if (scope === 'delegate' && row.key === 'delegate') return true;
    return false;
  }
  if (scope.kind === 'own' && row.key === 'own') return true;
  if (
    scope.kind === 'delegate' &&
    typeof row.parent !== 'string' &&
    row.parent.kind === 'delegate' &&
    row.parent.ownerId === scope.ownerId &&
    row.parent.collectionId === scope.collectionId
  ) {
    return true;
  }
  return false;
}

function isBareParentScope(scope: SearchScope, row: ParentRow): boolean {
  if (!scopeMatchesParent(scope, row)) return false;
  if (typeof scope === 'string') return true;
  if (scope.kind === 'own') return false; // narrowed own always carries visibility
  return scope.visibility === undefined;
}

function visibilityForRow(scope: SearchScope, row: ParentRow): Visibility | null {
  if (!scopeMatchesParent(scope, row)) return null;
  if (typeof scope === 'string') return null;
  if (scope.kind === 'own') return scope.visibility;
  return scope.visibility ?? null;
}

function makeNarrowed(row: ParentRow, vis: Visibility): SearchScope {
  if (row.key === 'own') return { kind: 'own', visibility: vis };
  if (typeof row.parent !== 'string' && row.parent.kind === 'delegate') {
    return { kind: 'delegate', ownerId: row.parent.ownerId, collectionId: row.parent.collectionId, visibility: vis };
  }
  // public / delegate-bare can't be narrowed; caller should never hit this branch.
  return row.parent;
}

// -- Component ---------------------------------------------------------------

export const ScopeSelector: React.FC<ScopeSelectorProps> = ({
  value,
  onChange,
  catalogBuckets,
}) => {
  const rows = useMemo<ParentRow[]>(
    () => (catalogBuckets && catalogBuckets.length > 0 ? bucketsToRows(catalogBuckets) : FALLBACK_ROWS),
    [catalogBuckets],
  );
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // Per-row selection state derived from the controlled `value`.
  const rowState = useMemo(() => {
    const state: Record<
      string,
      {
        bare: boolean;
        public: boolean;
        private: boolean;
      }
    > = {};
    for (const row of rows) {
      state[row.key] = { bare: false, public: false, private: false };
    }
    for (const s of value) {
      for (const row of rows) {
        if (!scopeMatchesParent(s, row)) continue;
        if (isBareParentScope(s, row)) state[row.key].bare = true;
        const vis = visibilityForRow(s, row);
        if (vis) state[row.key][vis] = true;
        break; // one scope can only belong to one row
      }
    }
    return state;
  }, [rows, value]);

  const setSelection = (next: SearchScope[]) => {
    onChange(canonicalizeScopes(next));
  };

  const toggleParent = (row: ParentRow) => () => {
    const st = rowState[row.key];
    const isAnyOn = st.bare || st.public || st.private;
    // Strip every scope owned by this row, then optionally add the bare parent.
    const remaining = value.filter((s) => !scopeMatchesParent(s, row));
    if (isAnyOn) {
      setSelection(remaining);
    } else {
      setSelection([...remaining, row.parent]);
    }
  };

  const toggleChild = (row: ParentRow, vis: Visibility) => () => {
    // Visual model: parent-checked = "all of this catalog" (children render
    // unchecked); narrowed children render checked only when their narrowed
    // token is present. Click semantics:
    //   - parent-checked + click child   → narrow to clicked child only
    //   - child-checked  + click child   → toggle off
    //   - child unchecked + click child  → add this child (sibling preserved;
    //     canonicalizeScopes collapses both-children into the bare parent)
    const st = rowState[row.key];
    const otherVis: Visibility = vis === 'public' ? 'private' : 'public';
    const remaining = value.filter((s) => !scopeMatchesParent(s, row));
    if (st.bare) {
      setSelection([...remaining, makeNarrowed(row, vis)]);
      return;
    }
    const next: SearchScope[] = [...remaining];
    if (!st[vis]) next.push(makeNarrowed(row, vis));
    if (st[otherVis]) next.push(makeNarrowed(row, otherVis));
    setSelection(next);
  };

  return (
    <Box
      sx={{
        px: 2.25,
        pt: 1.5,
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      data-testid="scope-selector"
    >
      <Typography
        component="h4"
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'text.secondary',
          mb: 1,
        }}
      >
        Catalog
      </Typography>
      <FormGroup aria-label="Catalog scope">
        {rows.map((row) => {
          const st = rowState[row.key];
          const childChecked = st.public || st.private;
          const parentChecked = st.bare;
          const indeterminate = !parentChecked && childChecked && !(st.public && st.private);
          const isExpanded = !!expanded[row.key];
          return (
            <Box key={row.key} data-testid={`catalog-row-${row.key}`}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  py: 0,
                }}
              >
                {row.expandable ? (
                  <IconButton
                    size="small"
                    onClick={() => toggleExpanded(row.key)}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    data-testid={`catalog-row-${row.key}-toggle`}
                    sx={{ p: 0.25 }}
                  >
                    {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                ) : (
                  <Box sx={{ width: 24 }} />
                )}
                <FormControlLabel
                  sx={{ m: 0, flex: 1, '& .MuiFormControlLabel-label': { flex: 1 } }}
                  control={
                    <Checkbox
                      size="small"
                      checked={parentChecked}
                      indeterminate={indeterminate}
                      onChange={toggleParent(row)}
                      inputProps={{ 'aria-label': row.label }}
                    />
                  }
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 1,
                      }}
                    >
                      <Typography component="span" sx={{ fontSize: '0.8125rem' }}>
                        {row.label}
                      </Typography>
                      {typeof row.count === 'number' && (
                        <Typography
                          component="span"
                          data-testid={`catalog-row-${row.key}-count`}
                          sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {row.count}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </Box>
              {row.expandable && (
                <Collapse in={isExpanded} unmountOnExit>
                  <Box sx={{ pl: 4 }}>
                    {(['public', 'private'] as const).map((vis) => {
                      const VisIcon = vis === 'public' ? PublicIcon : LockIcon;
                      const childCount = row.childCounts?.[vis];
                      const childKey = `catalog-row-${row.key}-${vis}`;
                      // Children render checked only for their narrowed
                      // token; parent-checked does not visually check the
                      // children (the click semantic is "narrow to this
                      // child", not "uncheck the all-selected sibling").
                      const checked = st[vis];
                      return (
                        <Box
                          key={vis}
                          data-testid={childKey}
                          sx={{ display: 'flex', alignItems: 'center', py: 0 }}
                        >
                          <FormControlLabel
                            sx={{ m: 0, flex: 1, '& .MuiFormControlLabel-label': { flex: 1 } }}
                            control={
                              <Checkbox
                                size="small"
                                checked={checked}
                                onChange={toggleChild(row, vis)}
                                inputProps={{
                                  'aria-label': `${row.label} ${vis === 'public' ? 'Public' : 'Private'}`,
                                }}
                              />
                            }
                            label={
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <VisIcon sx={{ fontSize: '0.95rem', color: 'text.secondary' }} />
                                  <Typography component="span" sx={{ fontSize: '0.8125rem' }}>
                                    {vis === 'public' ? 'Public' : 'Private'}
                                  </Typography>
                                </Box>
                                {typeof childCount === 'number' && (
                                  <Typography
                                    component="span"
                                    data-testid={`${childKey}-count`}
                                    sx={{
                                      fontSize: '0.75rem',
                                      color: 'text.secondary',
                                      fontVariantNumeric: 'tabular-nums',
                                    }}
                                  >
                                    {childCount}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })}
      </FormGroup>
    </Box>
  );
};

export default ScopeSelector;
